import { useScrollLock } from '@vueuse/core';

export interface MenuItem {
  title: string;
  alias: string;
  external: boolean;
  absolute: string;
  relative: string;
  existing: boolean;
  expanded: boolean;
  enabled: boolean;
  options: [];

  level_2?: MenuItem[];
  level_3?: MenuItem[];
  field_description?: { value: string }[];
  field_image?: {
    target_id: number;
    alt: string;
    title: string | null;
    width: number;
    height: number;
    target_type: string;
    target_uuid: string;
    url: string;
  }[];
  field_related_links: {
    uri: string;
    title: string;
    options: {
      external: boolean;
    };
  }[];
}

export function useMenu() {
  const { isSearchPanelOpen } = useDrupalSearch();

  const activeMenuItem = useState<MenuItem | null>(
    'drupal-active-menu-item',
    () => null
  );
  const isMegamenuOpen = useState<boolean>(
    'drupal-mega-menu-open',
    () => false
  );
  // Holds the sub-links of whichever top-level item is active
  const activeLevel2Item = useState<
    [MenuItem['title'] | null, MenuItem['level_3'] | null] | null
  >('drupal-active-menu-level_2', () => null);

  if (import.meta.client) {
    const isBodyScrollLocked = useScrollLock(document.body);

    watch(
      isMegamenuOpen,
      (isOpen) => {
        isBodyScrollLocked.value = isOpen;
      },
      { immediate: true }
    );
  }

  function handleToggleMegamenu(menuItem: MenuItem | null) {
    // If Search is open, close it and open the megamenu
    if (isSearchPanelOpen.value === true) isSearchPanelOpen.value = false;

    // When passing in null, close the megamenu
    if (menuItem === null) {
      isMegamenuOpen.value = false;
      activeMenuItem.value = null;
      activeLevel2Item.value = null;
      return;
    }

    // When menuTitle is the same as the active menu item, toggle the megamenu closed
    if (activeMenuItem.value?.title === menuItem.title) {
      activeMenuItem.value = null;
      isMegamenuOpen.value = false;
      activeLevel2Item.value = null;
      return;
    }

    // When menuTitle is different than the active menu item, set the active menu item and open the megamenu
    activeMenuItem.value = menuItem;
    isMegamenuOpen.value = true;
    activeLevel2Item.value = null;
  }

  function handleLevel2Selection(menuItem: MenuItem | null) {
    if (activeLevel2Item.value?.[0] === menuItem?.title) {
      activeLevel2Item.value = null;
    } else if (menuItem) {
      activeLevel2Item.value = [menuItem.title, menuItem.level_3];
    }
  }

  return {
    isMegamenuOpen,
    activeMenuItem,
    activeLevel2Item,
    handleToggleMegamenu,
    handleLevel2Selection,
  };
}
